import {
  concat,
  defer,
  EMPTY,
  empty,
  from,
  merge,
  Observable,
  of,
  Operator,
  OperatorFunction,
  Subscriber,
  throwError,
} from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';

export const from$ = from;

export const empty$ = empty;

// Replace empty$ with this code when we are ready
export const newEmpty$ = () => EMPTY;

export const concat$ = concat;

export const defer$ = defer;

// of$() is equals to of$(undefined)
export const of$ = (value?: any) => of(value);

export const throw$ = throwError;

export const merge$ = merge;

export function switchIfEmpty<T, R>(default$: Observable<R>): OperatorFunction<T, T | R> {
  return (source: Observable<T>) => source.lift(new SwitchIfEmptyOperator(default$));
}

export const asyncFilter$ = <T>(predicate: (value: T) => Observable<boolean>) => {
  return mergeMap((value: T) => {
    // Execute the predicate and if the result is true, then return the value
    return predicate(value).pipe(
      filter((result: boolean) => result),
      map(() => value),
    );
  });
};

class SwitchIfEmptyOperator<T, R> implements Operator<T, T | R> {
  constructor(private default$: Observable<R>) {}

  call(subscriber: Subscriber<T | R>, source: any): any {
    return source.subscribe(new SwitchIfEmptySubscriber(subscriber, this.default$));
  }
}

class SwitchIfEmptySubscriber<T, R> extends Subscriber<T> {
  private isEmpty: boolean = true;

  constructor(
    destination: Subscriber<T | R>,
    private default$: Observable<R>,
  ) {
    super(destination);
  }

  protected _complete(): void {
    if (this.isEmpty) {
      this.add(this.default$.subscribe(this.destination));
    } else {
      this.destination.complete();
    }
  }

  protected _next(value: T): void {
    this.isEmpty = false;
    this.destination.next(value);
  }
}

export function tapIndexed<T>(next: (value: T, index: number) => void): (source: Observable<T>) => Observable<T> {
  return (source: Observable<T>) => {
    return new Observable((observer) => {
      let index = 0;

      const subscription = source.subscribe({
        next: (value) => {
          next(value, index);
          index++;
          observer.next(value);
        },
        error: (err) => {
          observer.error(err);
        },
        complete: () => {
          observer.complete();
        },
      });

      return () => {
        subscription.unsubscribe();
      };
    });
  };
}
